import { useState } from 'react'
import { Mail, MessageCircle, Send } from 'lucide-react'

import { InnerHero } from '@/components/InnerHero'
import { Reveal } from '@/components/Reveal'

const contactEndpoint = import.meta.env.VITE_CONTACT_ENDPOINT?.trim() || '/api/contact'

export function ContactPage() {
  const [status, setStatus] = useState('idle')
  const [feedback, setFeedback] = useState('')

  const handleSubmit = async (event) => {
    event.preventDefault()

    const form = event.currentTarget

    setStatus('sending')
    setFeedback('')

    const formData = new FormData(form)
    const payload = Object.fromEntries(formData.entries())

    try {
      const response = await fetch(contactEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result.ok) {
        throw new Error(
          result.message || 'No pudimos enviar el mensaje. Inténtalo de nuevo.',
        )
      }

      form.reset()
      setStatus('success')
      setFeedback(result.message || 'Gracias. Tu mensaje fue enviado correctamente.')
    } catch (error) {
      setStatus('error')
      setFeedback(
        error instanceof Error
          ? error.message
          : 'No pudimos enviar el mensaje. Inténtalo de nuevo en unos minutos.',
      )
    }
  }

  return (
    <>
      <InnerHero
        eyebrow="Estamos para escucharte"
        title="Hablemos"
        description="Cuéntanos qué necesitas, comparte un recurso o propón una colaboración. Cada conversación puede abrir una nueva conexión."
      />

      <section className="section-space pt-0" aria-labelledby="contact-form-title">
        <div className="container-wide story-grid">
          <Reveal>
            <div className="rounded-[3rem] bg-ink-deep p-7 text-white md:p-12">
              <p className="eyebrow !text-apricot before:!bg-apricot">Contacto</p>
              <h2 className="section-title" id="contact-form-title">
                Tu mensaje importa.
              </h2>
              <p className="lede mt-6 !text-white/65">
                Describe tu situación con el detalle que te resulte cómodo. No incluyas
                información médica sensible que no sea necesaria.
              </p>
              <div className="mt-10 grid gap-5">
                <div className="flex items-start gap-4">
                  <span className="grid size-12 shrink-0 place-items-center rounded-full bg-orange">
                    <MessageCircle aria-hidden="true" size={22} />
                  </span>
                  <div>
                    <h3 className="m-0 font-display text-2xl">Familias</h3>
                    <p className="mt-1 text-sm leading-relaxed text-white/60">
                      Preguntas, orientación y experiencias compartidas.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="grid size-12 shrink-0 place-items-center rounded-full bg-violet">
                    <Mail aria-hidden="true" size={22} />
                  </span>
                  <div>
                    <h3 className="m-0 font-display text-2xl">Alianzas</h3>
                    <p className="mt-1 text-sm leading-relaxed text-white/60">
                      Profesionales, organizaciones y proyectos con propósito común.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form className="glass-card p-6 md:p-10" onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="name">Nombre</label>
                  <input
                    id="name"
                    name="name"
                    autoComplete="name"
                    minLength={2}
                    maxLength={100}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="email">Correo electrónico</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    maxLength={254}
                    required
                  />
                </div>
                <div className="field field-full">
                  <label htmlFor="topic">Quiero hablar sobre</label>
                  <select id="topic" name="topic" defaultValue="familia">
                    <option value="familia">Orientación para una familia</option>
                    <option value="recurso">Recomendar un recurso</option>
                    <option value="alianza">Proponer una alianza</option>
                    <option value="donativo">Donativos</option>
                    <option value="otro">Otro tema</option>
                  </select>
                </div>
                <div className="field field-full">
                  <label htmlFor="message">Mensaje</label>
                  <textarea
                    id="message"
                    name="message"
                    minLength={10}
                    maxLength={3000}
                    required
                  />
                </div>
                <div className="contact-trap" aria-hidden="true">
                  <label htmlFor="website">Sitio web</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    autoComplete="off"
                    tabIndex={-1}
                  />
                </div>
                <label className="field-full flex items-start gap-3 text-sm leading-relaxed text-ink-deep/65">
                  <input className="mt-1 size-4" name="privacy" type="checkbox" required />
                  <span>
                    Acepto que mis datos se utilicen únicamente para responder este
                    mensaje.
                  </span>
                </label>

                {status === 'success' ? (
                  <p className="form-status" role="status" aria-live="polite">
                    {feedback}
                  </p>
                ) : null}
                {status === 'error' ? (
                  <p className="form-status !bg-apricot" role="alert">
                    {feedback}
                  </p>
                ) : null}

                <button
                  className="button button-primary field-full mt-2 w-fit cursor-pointer disabled:cursor-wait disabled:opacity-60"
                  type="submit"
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'Enviando…' : 'Enviar mensaje'}
                  <Send aria-hidden="true" size={17} />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  )
}
